import { DependencyList, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApi } from '@tymely/api';
import { datadogLogs } from '@datadog/browser-logs';
import { useAtomValue, useSetAtom, atom } from 'jotai';
import { IIdleTimer, useIdleTimer } from 'react-idle-timer';
import { DOCUMENT_TITLE, IDLE_TIMER_TIMEOUT } from '@tymely/config';
import { getSeconds } from '@tymely/utils';
import { IAppVersion, isLastTicketSubmittedAtom } from '@tymely/atoms';

import { useSelectedComment } from './comment.services';
import { useTicket } from './ticket.services';

export const handlingTimerAtom = atom<IIdleTimer | undefined>(undefined);

export const useHandlingTimer = () => useAtomValue(handlingTimerAtom);

export const useTimeHandler = () => {
    const newHandlingTimer = useIdleTimer({ timeout: IDLE_TIMER_TIMEOUT, startOnMount: true });
    const handlingTimer = useHandlingTimer() || newHandlingTimer;
    const setHandlingTimer = useSetAtom(handlingTimerAtom);
    useEffect(() => {
        setHandlingTimer(handlingTimer);
    }, []);
    return handlingTimer;
};

export const useLogTicketProcess = () => {
    const ticket = useTicket();
    const comment = useSelectedComment();
    const handlingTimer = useTimeHandler();
    const isLastTicketSubmitted = useAtomValue(isLastTicketSubmittedAtom);

    useEffect(() => {
        if (ticket) {
            datadogLogs.setGlobalContextProperty('ticket_id', ticket.id);
        }
    }, [ticket?.id]);

    useEffect(() => {
        datadogLogs.setGlobalContextProperty('is_last_ticket_submitted', isLastTicketSubmitted);
    }, [isLastTicketSubmitted]);

    const logProcessed = (action: string) => {
        if (ticket) {
            datadogLogs.logger.info('Ticket has been processed', {
                action: action,
                ticket_id: ticket.id,
                organization_id: ticket.organization_id,
                handling_duration: getSeconds(handlingTimer.getElapsedTime()),
                idle_duration: getSeconds(handlingTimer.getIdleTime()),
                active_duration: getSeconds(handlingTimer.getActiveTime()),
            });
        }
    };

    return {
        logFailed: (wfId: number, actions: string[]) => {
            if (ticket) {
                datadogLogs.logger.info('Reviewer marked ticket verification failed', {
                    ticket_id: ticket.id,
                    organization_id: ticket?.organization_id,
                    exception: `Failed actions: ${actions.join(', ')}`,
                    policy_set_id: comment?.selected_intent_id,
                    workflow_id: wfId,
                });
            }
        },
        logProcessed,
        logSentToQueue: (queueId: number, queueName: string) => {
            if (ticket) {
                datadogLogs.logger.info(`Ticket sent to ${queueName} queue`, {
                    ticket_id: ticket.id,
                    organization_id: queueId,
                });
            }
        },
    };
};

export const THIRD_PARTY_SECRETS_QUERY_KEY = 'third-party-secrets';

export const useThirdPartySecretsQuery = (keys: string[]) => {
    const api = useApi();
    return useQuery({
        queryKey: [THIRD_PARTY_SECRETS_QUERY_KEY],
        queryFn: () => api.post('third-party/service-provider-secrets', { keys }) as Promise<any>,
    });
};

export const APP_VERSION_QUERY_KEY = 'appVersion';

export const useAppVersionQuery = () => {
    const api = useApi();
    return useQuery({
        queryKey: [APP_VERSION_QUERY_KEY],
        queryFn: () => api.post('online/app-version') as Promise<IAppVersion>,
        placeholderData: { dinesh_version: '' },
        refetchInterval: 30 * 60 * 1000, // thirty minutes
        refetchIntervalInBackground: true,
    });
};

export const isMonitoringEnabled = () => {
    return import.meta.env.VITE_DATADOG_APPLICATION_ID;
};

export const useDocumentTitle = (title: string, deps?: DependencyList) => {
    useEffect(() => {
        document.title = `${title} | ${DOCUMENT_TITLE}`;
        return () => {
            document.title = DOCUMENT_TITLE;
        };
    }, deps ?? []);
};
